import { useSelector } from 'react-redux'
import { OfferPopup as RfOfferPopup } from '@renderforest/rf-components'
import type {
  TOfferNotification,
  TOfferPriceContent
} from '@renderforest/rf-components'

import { getTariffPlan_API, markNotificationsRead_API } from 'api'
import { campaignSelectors } from 'store/features/campaign/selectors'
import { ICampaign } from 'store/types/campaign'

// Resolves the offer's promo-badge prices from the tariff-plans API by the
// campaign's planId. The badge shows the plan's pricing for the campaign's
// pricingPeriod exactly as the API returns it: the regular `price` crossed
// out against `discountedPrice` (no client-side math on either), prefixed
// with the API's currencySymbol. A plan without a discount shows just the
// regular price. No planId on the campaign or missing price data simply
// means the popup renders without the badge.
const getPriceContent = async (
  notification: TOfferNotification
): Promise<TOfferPriceContent | null> => {
  if (typeof notification.planId !== 'number') return null

  const response = await getTariffPlan_API(notification.planId)
  const priceData = response.data?.priceData
  const isMonthly = notification.pricingPeriod === 'monthly'
  const tier = isMonthly ? priceData?.monthly : priceData?.yearly

  if (typeof tier?.price !== 'number') return null

  const symbol = tier.currencySymbol ?? priceData?.currencySymbol ?? ''

  return {
    oldPrice: tier.discountedPrice ? `${symbol}${tier.price}` : null,
    newPrice: `${symbol}${tier.discountedPrice || tier.price}`,
    period: isMonthly ? '/ mo' : '/ yr'
  }
}

// The popup is fed explicitly from the campaign state rather than letting
// rf-components read /notifications itself, so it shares the single request
// the campaign listener already makes. Shown while the campaign notification
// is unread; it is marked read only when the user closes the popup or clicks
// its CTA (see onClose/onAction).
const buildOfferNotification = (
  campaign: ICampaign | null
): TOfferNotification | null => {
  const endDate = campaign?.endDate ?? campaign?.content?.endDate
  const subTitle = campaign?.content?.subTitle

  if (!campaign || campaign.status !== 'unread' || !endDate || !subTitle) {
    return null
  }

  return {
    // appNotificationId intentionally omitted (optional since
    // rf-components@1.0.16): without an id the library skips its
    // mark-as-read-on-open — the read state is set only on an intentional
    // close (X / overlay click) or a CTA click, via onClose/onAction below.
    endDate,
    subTitle,
    title: campaign.content?.title,
    text: campaign.content?.text,
    imageSrc: campaign.content?.imageSrc,
    // Promo video takes the hero image's place; imageSrc then acts as poster
    videoSrc: campaign.videoSrc ?? campaign.content?.videoSrc,
    buttonTitle: campaign.content?.buttonTitle,
    buttonUrl: campaign.content?.buttonUrl,
    planId: campaign.planId,
    pricingPeriod:
      campaign.pricingPeriod === 'monthly' ||
      campaign.pricingPeriod === 'yearly'
        ? campaign.pricingPeriod
        : null
  }
}

// Mounted only when there is an unread campaign to show, fed by
// useCampaignListener from the notifications API and live socket pushes.
const OfferPopup = () => {
  const campaign = useSelector(campaignSelectors.campaign)
  const notification = buildOfferNotification(campaign)

  if (!campaign || !notification) return null

  const markAsRead = () => {
    markNotificationsRead_API([campaign.id]).catch(() => {})
  }

  const onAction = () => {
    markAsRead()
    const buttonUrl = campaign.content?.buttonUrl
    if (buttonUrl) window.location.href = buttonUrl
  }

  return (
    <RfOfferPopup
      // Remount on campaign change so a dismissed popup re-arms for a new one
      key={campaign.id}
      notification={notification}
      onClose={markAsRead}
      onAction={onAction}
      getPriceContent={getPriceContent}
    />
  )
}

export default OfferPopup
